import { useState } from 'react';
import { createPortal } from 'react-dom';
import type { TicketStats } from '../lib/api';

interface ProjectProgressBarProps {
  stats: TicketStats;
  height?: 'sm' | 'md';
}

export function ProjectProgressBar({ stats, height = 'sm' }: ProjectProgressBarProps) {
  const [tooltip, setTooltip] = useState<{ x: number; y: number } | null>(null);
  const {
    total, complete, inProgress, inProgressNearDue, inProgressPastDue = 0,
    todo, todoNearDue, todoPastDue = 0,
    nearDue, pastDue = [],
  } = stats;

  if (total === 0) return null;

  const pct = (n: number) => `${Math.max((n / total) * 100, n > 0 ? 1.5 : 0)}%`;

  const h = height === 'md' ? 'h-2.5' : 'h-1.5';

  const regularInProgress = inProgress - inProgressNearDue - inProgressPastDue;
  const regularTodo = todo - todoNearDue - todoPastDue;
  const hasHoverData = nearDue.length > 0 || pastDue.length > 0;

  const onEnter = (e: React.MouseEvent) => setTooltip({ x: e.clientX, y: e.clientY });
  const onMove = (e: React.MouseEvent) => setTooltip({ x: e.clientX, y: e.clientY });
  const onLeave = () => setTooltip(null);

  return (
    <div className="w-full">
      {/* Bar */}
      <div className={`flex w-full ${h} rounded-full overflow-hidden bg-gray-100 dark:bg-white/5`}>
        {complete > 0 && (
          <div
            style={{ width: pct(complete) }}
            className="bg-green-500 dark:bg-green-400 flex-shrink-0 transition-all"
          />
        )}
        {regularInProgress > 0 && (
          <div
            style={{
              width: pct(regularInProgress),
              backgroundImage: 'repeating-linear-gradient(-45deg, #f59e0b, #f59e0b 3px, #fcd34d 3px, #fcd34d 6px)',
            }}
            className="flex-shrink-0 transition-all"
          />
        )}
        {/* Past-due in-progress — red */}
        {inProgressPastDue > 0 && (
          <div
            style={{ width: pct(inProgressPastDue) }}
            className="bg-red-500 flex-shrink-0 transition-all cursor-pointer"
            onMouseEnter={onEnter}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
          />
        )}
        {/* Near-due in-progress — orange */}
        {inProgressNearDue > 0 && (
          <div
            style={{ width: pct(inProgressNearDue) }}
            className="bg-orange-500 flex-shrink-0 transition-all cursor-pointer"
            onMouseEnter={onEnter}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
          />
        )}
        {/* Past-due todo — red (slightly lighter) */}
        {todoPastDue > 0 && (
          <div
            style={{ width: pct(todoPastDue) }}
            className="bg-red-400 flex-shrink-0 transition-all cursor-pointer"
            onMouseEnter={onEnter}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
          />
        )}
        {/* Near-due todo — orange */}
        {todoNearDue > 0 && (
          <div
            style={{ width: pct(todoNearDue) }}
            className="bg-orange-400 flex-shrink-0 transition-all cursor-pointer"
            onMouseEnter={onEnter}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
          />
        )}
        {regularTodo > 0 && (
          <div
            style={{ width: pct(regularTodo) }}
            className="bg-gray-200 dark:bg-white/10 flex-shrink-0 transition-all"
          />
        )}
      </div>

      {/* Tooltip — portaled to body, follows cursor */}
      {tooltip && hasHoverData && createPortal(
        <div
          style={{
            position: 'fixed',
            zIndex: 9999,
            pointerEvents: 'none',
            left: Math.min(tooltip.x + 12, window.innerWidth - 300),
            top: Math.max(tooltip.y - 12, 8),
            transform: 'translateY(-100%)',
          }}
        >
          <div className="bg-gray-900 dark:bg-gray-800 text-white text-micro rounded-lg px-3 py-2 shadow-xl border border-white/10" style={{ maxWidth: 280 }}>
            {pastDue.length > 0 && (
              <>
                <p className="font-medium text-red-400 mb-1">Past due</p>
                {pastDue.map((t) => (
                  <div key={t.ticketId} className="mb-1 last:mb-0">
                    <span className="text-gray-100">{t.title}</span>
                    <span className="text-gray-400 ml-1.5">· Due {t.dueDate}</span>
                  </div>
                ))}
              </>
            )}
            {pastDue.length > 0 && nearDue.length > 0 && (
              <div className="border-t border-white/10 my-1.5" />
            )}
            {nearDue.length > 0 && (
              <>
                <p className="font-medium text-orange-400 mb-1">Near due</p>
                {nearDue.map((t) => (
                  <div key={t.ticketId} className="mb-1 last:mb-0">
                    <span className="text-gray-100">{t.title}</span>
                    <span className="text-gray-400 ml-1.5">· Due {t.dueDate}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>,
        document.body,
      )}

      {/* Legend */}
      {height === 'md' && total > 0 && (
        <div className="flex items-center gap-4 mt-2 text-meta text-gray-500 dark:text-gray-400">
          <span>{complete} complete</span>
          <span>{inProgress} in progress</span>
          <span>{todo} todo</span>
          {pastDue.length > 0 && <span className="text-red-500">{pastDue.length} past due</span>}
          {nearDue.length > 0 && <span className="text-orange-500">{nearDue.length} near due</span>}
        </div>
      )}
    </div>
  );
}
